// Deal Pipeline spec §5.4 — minimal CSV exporter for the Pipeline + Archive
// table views. Client-side only (no backend roundtrip), so the export
// always reflects the user's filtered ROW SET exactly. Excel-safe via the
// "﻿" BOM and RFC4180 quoting.
//
// 🔴 DATES ARE EMITTED AS THE RAW WIRE VALUE, ON PURPOSE, AND THAT IS WHY
// THE FILE AND THE SCREEN DISAGREE. `stageEnteredDate` and `createdAt` are
// `DateTimeOffset` instants; every screen renders them in the viewer's
// configured zone, and this file does not — so a row created at 18:00
// Phoenix reads as the NEXT day here. That was flagged as a defect and is
// being recorded as a decision instead, because the alternative is worse: a
// CSV is a data-interchange file, and a localised string is lossy (it drops
// the offset, so nothing can reconstruct the instant) and unstable (it
// changes with whoever exported it). ISO 8601 is the only form that
// round-trips, and Excel parses it.
//
// The sibling exporter already states the same principle for money —
// `allocationExport.ts`: "amounts are raw numbers so the spreadsheet can do
// its own math" — so this extends an established convention rather than
// inventing one. What was genuinely missing is that nobody was TOLD, which
// is what the "(UTC)" suffixes on the two instant columns fix. A
// wall-calendar column would need no suffix and must not get one: it has no
// zone to name.
//
// If you are about to route these through `formatDateInTz` to make the file
// match the screen: don't. Add a second column, or export the instant and
// let the reader format it.

import type { DealListItemDto } from "@/types/deals";

export interface ColumnSpec<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

export const DEAL_COLUMNS: ColumnSpec<DealListItemDto>[] = [
  { header: "Deal #", value: (d) => d.dealNumber },
  { header: "Business", value: (d) => d.businessNameInitial ?? "" },
  { header: "Stage", value: (d) => d.stage },
  { header: "Sub-state", value: (d) => d.subState },
  { header: "Purchase price", value: (d) => d.purchasePrice },
  { header: "Seller note", value: (d) => d.sellerNoteAmount },
  { header: "Assigned C1", value: (d) => d.assignedC1Id ?? "" },
  { header: "Assigned B1", value: (d) => d.assignedB1Id ?? "" },
  { header: "Assigned A1", value: (d) => d.assignedA1Id ?? "" },
  { header: "Stage entered (UTC)", value: (d) => d.stageEnteredDate },
  { header: "Created (UTC)", value: (d) => d.createdAt },
  { header: "Archived", value: (d) => (d.isArchived ? "Y" : "N") },
];

export function quote(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildCsv<T>(rows: T[], columns: ColumnSpec<T>[]): string {
  const head = columns.map((c) => quote(c.header)).join(",");
  const body = rows
    .map((r) => columns.map((c) => quote(c.value(r))).join(","))
    .join("\n");
  return `﻿${head}\n${body}`;
}

/** Trigger a browser download of already-built CSV text. */
export function downloadCsvFile(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadCsv(
  filename: string,
  rows: DealListItemDto[],
  columns: ColumnSpec<DealListItemDto>[] = DEAL_COLUMNS,
) {
  downloadCsvFile(filename, buildCsv(rows, columns));
}
